import React, { useEffect, useMemo, useState } from 'react';
import { environment } from 'nsdi-volto-frontend/config/environments';
import { normalizeHierarchyTreeBase } from '../../Blocks/VoyagerSearchPageBlock/Services/treeHelpers';
import {
  buildHierarchyTreeBaseFacetUrl,
  buildHierarchyTreeBaseOptions,
} from './hierarchyTreeBaseWidgetHelpers';
import type { HierarchyTreeBaseOption } from './hierarchyTreeBaseWidgetHelpers';

type HierarchyTreeBaseWidgetProps = {
  id: string;
  title?: string;
  description?: string;
  required?: boolean;
  value?: string;
  error?: string[] | string;
  disabled?: boolean;
  formData?: {
    voyagerBaseUrl?: string;
    voyagerConfigId?: string;
  };
  onChange: (id: string, value: string) => void;
};

type TreeFacetResponse = {
  facet_counts?: {
    facet_fields?: {
      tree_nsdi?: Array<string | number>;
    };
  };
};

const HierarchyTreeBaseWidget: React.FC<HierarchyTreeBaseWidgetProps> = ({
  id,
  title,
  description,
  required,
  value,
  error,
  disabled,
  formData,
  onChange,
}) => {
  const [options, setOptions] = useState<HierarchyTreeBaseOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const solrSelectUrl = useMemo(() => {
    const baseUrl = (
      formData?.voyagerBaseUrl || environment.voyager.baseUrl
    ).replace(/\/$/, '');

    return baseUrl.endsWith('/solr/v0/select')
      ? baseUrl
      : `${baseUrl}/solr/v0/select`;
  }, [formData?.voyagerBaseUrl]);
  const configId = formData?.voyagerConfigId || environment.voyager.configId;
  const normalizedValue = useMemo(
    () => normalizeHierarchyTreeBase(value) || '',
    [value],
  );

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);
    setFetchError('');

    fetch(
      buildHierarchyTreeBaseFacetUrl({
        solrSelectUrl,
        configId,
      }),
      { cache: 'no-store', signal: controller.signal },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Hierarchy request failed: ${response.status}`);
        }
        return response.json() as Promise<TreeFacetResponse>;
      })
      .then((data) => {
        setOptions(
          buildHierarchyTreeBaseOptions(
            data.facet_counts?.facet_fields?.tree_nsdi || [],
          ),
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          setFetchError('Unable to load hierarchy choices.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [configId, solrSelectUrl]);

  const hasSelectedOption = options.some(
    (option) => option.value === normalizedValue,
  );
  const hasError = Array.isArray(error) ? error.length > 0 : Boolean(error);

  return (
    <div
      className={`field hierarchy-tree-base-widget ${hasError ? 'error' : ''}`}
    >
      <label htmlFor={id}>
        {title}
        {required ? ' *' : null}
      </label>
      {description ? <p className="help">{description}</p> : null}
      <select
        id={id}
        value={normalizedValue}
        disabled={disabled || isLoading}
        onChange={(event) => onChange(id, event.target.value)}
      >
        <option value="">
          {isLoading ? 'Loading hierarchy choices...' : 'Use full hierarchy'}
        </option>
        {normalizedValue && !hasSelectedOption ? (
          <option value={normalizedValue}>{normalizedValue}</option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {fetchError ? <p className="help">{fetchError}</p> : null}
      {hasError ? (
        <div className="ui pointing red basic label">
          {Array.isArray(error) ? error.join(', ') : error}
        </div>
      ) : null}
    </div>
  );
};

export default HierarchyTreeBaseWidget;
